import type { ReactNode } from 'react';
import React, { useContext, createContext, useMemo } from 'react';

import { useGetUserProfile } from 'state/remote/remote-freelancer/use-get-user-profile';

import type { Freelancer, Document, User } from 'state/__generated__/tadaahAPI';

import type { FreelancerSignupStatusResult } from 'state/remote/remote-freelancer/use-get-freelancer-signup-status';
import useGetFreelancerDocuments from 'state/remote/remote-freelancer/use-get-freelancer-documents';
import { useGetFreelancerDetails } from 'state/remote/remote-freelancer/use-get-freelancer-details';
import useGetFreelancerSignupStatus from 'state/remote/remote-freelancer/use-get-freelancer-signup-status';

type FreelancerSignUpContext = {
  profile: User | undefined;
  profileIsSuccess: boolean | undefined;
  freelancerDetails: Freelancer | undefined;
  detailsIsSuccess: boolean | undefined;
  personalRegisterDocument: Document | undefined;
  insuranceDocument: Document | undefined;
  diplomaDocuments: Document[] | undefined;
  certificationDocuments: Document[] | undefined;
  signUpStatus: FreelancerSignupStatusResult | undefined;
  statusLoading: boolean | undefined;
  statusIsSuccess: boolean | undefined;
};

const freelancerSignUpContextInitialValue = {
  certificationDocuments: [] as Document[],
  detailsIsSuccess: false,
  diplomaDocuments: [] as Document[],
  freelancerDetails: undefined,
  insuranceDocument: undefined,
  personalRegisterDocument: undefined,
  profile: undefined,
  profileIsSuccess: false,
  signUpStatus: undefined,
  statusIsSuccess: false,
  statusLoading: false,
};

const FreelancerSignUpCtx = createContext<FreelancerSignUpContext>(
  freelancerSignUpContextInitialValue,
);

const FreelancerSignUpContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { data: profile, isSuccess: profileIsSuccess } = useGetUserProfile();
  const { data: freelancerDetails, isSuccess: detailsIsSuccess } =
    useGetFreelancerDetails();
  const { data: documents } = useGetFreelancerDocuments();
  const {
    data: signUpStatus,
    isLoading: statusLoading,
    isSuccess: statusIsSuccess,
  } = useGetFreelancerSignupStatus();

  const FreelancerSignUpContext = useMemo((): FreelancerSignUpContext => {
    return {
      certificationDocuments: documents,
      detailsIsSuccess,
      diplomaDocuments: documents,
      freelancerDetails,
      insuranceDocument: undefined,
      personalRegisterDocument: undefined,
      profile,
      profileIsSuccess,
      signUpStatus,
      statusIsSuccess,
      statusLoading,
    };
  }, [
    documents,
    detailsIsSuccess,
    freelancerDetails,
    profile,
    profileIsSuccess,
    signUpStatus,
    statusIsSuccess,
    statusLoading,
  ]);

  return (
    <FreelancerSignUpCtx.Provider value={FreelancerSignUpContext}>
      {children}
    </FreelancerSignUpCtx.Provider>
  );
};

export const useFreelancerSignUp = (): FreelancerSignUpContext =>
  useContext(FreelancerSignUpCtx);

export default {
  Consumer: FreelancerSignUpCtx.Consumer,
  Provider: FreelancerSignUpContextProvider,
};
